import { types, getEnv, flow, Instance, SnapshotOut } from 'mobx-state-tree';
import { CategoryEventsStore } from './models/eventListModel';
import { IAppEnv } from './appEnvironment';
import { LinksStore } from './models/links';
import { HomeStore } from './models/home';
import { CategoryMenuStore } from './models/mainCategoryMenu';
import { eventDetailStore } from './models/eventDetail';
import { BannerStore } from './models/banner';
import { ReportedProblemType } from './api/api';
import {
    IGroupedApiCtx, ISportsBettingSessionApiForDesktop, TodayTicketsStore, TicketListStore,
    TicketDetailStore, defaultTicketDetailStore, ListPagerModel, CashOutListStore, SoundUI, ConnectionStatus, TicketListForVGStore, TicketDetailForVGStore, defaultTicketDetailForVGStore,
    TicketListTypeSelectorStore, TicketListTypeSelectorItemStore, cashOutDtos, ScreenInfo, gAService, ApplicationStoreWithFooterVisibility, FindTicketByNumberStore, RightColumnBannerStore,
    ILoggingService,
    devLogService
} from 'eTip--common';
import { MtsTicketStore } from '../eTipCommon/models/mtsTicket/mtsTicketStore';
import { UpcomigLiveEventsStore } from './models/upcomingLiveEvents';
import { paths } from './routing';
import { translationForMts } from '../../localization/localization';
import { MarketingActionStoreDesktop } from './models/marketingActionStore';
import { betDetailStore } from './models/betDetail';
import { chatConfigService } from '../eTipCommon/models/chat/setup';
import { LiveEventInPrematchListStore } from './models/live/liveEventInPrematchList';

export { Provider } from 'mobx-react';

export type IApplicationStore = Instance<typeof ApplicationStore>;

export const ApplicationStoreKey = 'applicationStore';
export const ApplicationStore = types.compose(ApplicationStoreWithFooterVisibility,
    types.model(ApplicationStoreKey, {
        mainMenu: types.optional(CategoryMenuStore, <SnapshotOut<typeof CategoryMenuStore>>{}),
        events: types.optional(CategoryEventsStore, <SnapshotOut<typeof CategoryEventsStore>>{}),
        liveInPrematchEvents: types.maybeNull(LiveEventInPrematchListStore),
        eventDetail: types.optional(eventDetailStore, <SnapshotOut<typeof eventDetailStore>>{}),
        links: types.optional(LinksStore, <SnapshotOut<typeof LinksStore>>{
            sports: [],
            activeTab: '0'
        }),
        banner: types.optional(BannerStore, <SnapshotOut<typeof BannerStore>>{
            items: []
        }),
        home: types.optional(HomeStore, <SnapshotOut<typeof HomeStore>>{
            tabs: []
        }),
        screenInfo: types.optional(ScreenInfo, <SnapshotOut<typeof ScreenInfo>>{}),
        ticketWrapper: types.maybeNull(MtsTicketStore), //for __IsMTS__ only
        betDetail: types.optional(betDetailStore, <SnapshotOut<typeof betDetailStore>>{}), // for __IsMTSTipos__ only TODOHMTS
        todayTickets: types.maybeNull(TodayTicketsStore), //for __IsMTS__ only
        cashOutList: types.maybeNull(CashOutListStore), //for __IsMTS__ only
        ticketList: types.optional(TicketListStore, <SnapshotOut<typeof TicketListStore>>{
            pager: <SnapshotOut<typeof ListPagerModel>>{}
        }),
        ticketDetail: types.optional(TicketDetailStore, defaultTicketDetailStore),
        ticketForVG: types.maybeNull(TicketDetailForVGStore),
        ticketListForVG: types.maybeNull(TicketListForVGStore), //for __IsMTS__ only
        ticketListTypeSelector: types.maybeNull(TicketListTypeSelectorStore), //for __IsMTS__ only
        findTicketNumber: types.maybeNull(FindTicketByNumberStore), //for __IsMTS__ only
        upcomigLiveEventsStore: types.maybeNull(UpcomigLiveEventsStore),
        soundUI: types.maybeNull(SoundUI),
        connectionStatus: types.string.create(ConnectionStatus.Connected.toString()),
        isOfflineCheckInProgress: types.optional(types.boolean, false),
        rightColumnBanners: types.maybeNull(RightColumnBannerStore),
        marketingAction: types.maybeNull(MarketingActionStoreDesktop),
    })
        .views(self => {
            const env = getEnv<IAppEnv>(self);
            return {
                get dateTimeService() {
                    return getEnv<IAppEnv>(self).dateTimeService;
                },
                get settingsProvider() {
                    return getEnv<IAppEnv>(self).settings;
                },
                get canShowAddPublicLinkToTicketToChat(): boolean {
                    return env.loggedUserId > 0 && chatConfigService.canShowChatOnStart();
                },
                get canShowMobileAppAdvert() {
                    return env.settings.canShowMobileAppAdvert;
                },
                get localStorage() {
                    return env.localStorage;
                },
                get loggingService(): ILoggingService {
                    return env.loggingService;
                }
            }
        })
        .actions(self => {
            const env = getEnv<IAppEnv>(self);
            const errorInterval = 10 * 1000;

            const login = flow(function* () {
                let ctx: IGroupedApiCtx<ISportsBettingSessionApiForDesktop> | null = null;
                try {
                    if (!ctx || !ctx.isOpen()) {
                        ctx = env.sessionApi.open();
                    }
                    const response = yield ctx.api.playerSignInAsync({ login: '', password: '' });
                    console.log('r', response);
                } catch (error) {
                } finally {
                    ctx = null;
                }
            });

            const setConnectionStatus = (connectionStatus: ConnectionStatus) => {
                self.connectionStatus = connectionStatus.toString();
            }

            const checkOfflineAsync: () => Promise<void> = flow(function* () {
                //same in liveStores
                if (self.isOfflineCheckInProgress) {
                    throw new Error("Already in action");
                }
                self.isOfflineCheckInProgress = true;
                try {
                    yield env.heartbeatService.forceCheckAsync();
                } finally {
                    self.isOfflineCheckInProgress = false;
                }
            });

            const handleCashoutChange = (data: cashOutDtos.CashOutResponse) => {
                devLogService.log("handleCashoutChange" + data.Bets.length);
                self.cashOutList.setData(data);
                self.ticketDetail.cashOutChanged(data);
                //self.todayTickets.setCashouts(data);
            };

            /**
                * Subscribes to MTS cashout, sRNotificationHandler resubscribes if reconnected.
            */
            const subscribeToMtsCashoutAsync: () => Promise<void> = flow(function* () {

                try {
                    self.cashOutList.setIsLoading();
                    let data: cashOutDtos.CashOutResponse = yield env.sRNotificationHandler.subscribeForCashoutAsync(handleCashoutChange);
                    handleCashoutChange(data);
                }
                catch (e) {
                    env.loggingService.error("subscribeToCashout failed", e);  // can't do more, reconnect done in SRNotificationHandler
                    self.cashOutList.setIsError();
                }
            });

            return {
                initialize() {
                    self.screenInfo.init();
                    const pathsInstance = paths();
                    const translation = translationForMts();
                    self.ticketWrapper = MtsTicketStore.create(<SnapshotOut<typeof MtsTicketStore>>{}, env);
                    self.ticketWrapper.init(5);

                    self.todayTickets = TodayTicketsStore.create(<SnapshotOut<typeof TodayTicketsStore>>{}, env);
                    self.cashOutList = CashOutListStore.create(<SnapshotOut<typeof CashOutListStore>>{ isLoading: true, isAvailable: false }, env);

                    if (env.settings.canShowVirtualGamesTickets) {
                        self.ticketListTypeSelector = TicketListTypeSelectorStore.create(<Instance<typeof TicketListTypeSelectorStore>>{
                            items: [TicketListTypeSelectorItemStore.create(<Instance<typeof TicketListTypeSelectorItemStore>>{ title: translation.myTickets_TypeSports, path: pathsInstance.ticketList }),
                            TicketListTypeSelectorItemStore.create(<Instance<typeof TicketListTypeSelectorItemStore>>{ title: translation.myTickets_TypeVirtualGames, path: pathsInstance.ticketListVG })],
                            activeIndex: 0 //TODO
                        }, env);

                        self.ticketListForVG = TicketListForVGStore.create(<Instance<typeof TicketListForVGStore>>{
                            pager: <Instance<typeof ListPagerModel>>{}
                        }, env);

                        self.ticketForVG = defaultTicketDetailForVGStore();
                    }

                    self.findTicketNumber = __IsMTSTipos__ ? null : FindTicketByNumberStore.create(<Instance<typeof FindTicketByNumberStore>>{}, env); //TODOHMTS create setting
                    self.upcomigLiveEventsStore = UpcomigLiveEventsStore.create(<Instance<typeof UpcomigLiveEventsStore>>{}, env);

                    self.soundUI = SoundUI.create(<Instance<typeof SoundUI>>{ isMute: true }, env);
                    self.soundUI.initFromSettings();

                    self.rightColumnBanners = RightColumnBannerStore.create(<Instance<typeof RightColumnBannerStore>>{}, env);

                    if (__IsMTSTipos__) {
                        self.marketingAction = MarketingActionStoreDesktop.create(<Instance<typeof MarketingActionStoreDesktop>>{}, env);
                    }

                    if (env.settings.canShowLiveInPrematch) {
                        self.liveInPrematchEvents = LiveEventInPrematchListStore.create(/*<Instance<typeof LiveEventInPrematchListStore>> */{}, env);
                    }
                },
                saveError(error: Error, info: Object, isFatal: boolean) {
                    let stringifiedError = JSON.stringify({ msg: error.message, stack: error.stack }); //simple stringify(error) returns ""
                    let stringifiedInfo = JSON.stringify(info);

                    gAService.sendException(
                        error.message + " / " + stringifiedError.substr(0, 100), //https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#exDescription
                        isFatal);

                    const ctx = env.api.open();
                    ctx.api.reportProblemToServer(ReportedProblemType.Error, `${stringifiedError}: ${stringifiedInfo}`, errorInterval);
                },
                login,
                subscribeToMtsCashoutAsync,
                setConnectionStatus,
                checkOfflineAsync
            };
        }));
